import { effectiveRir, EFFORT_HARD_SET_RIR } from "./effortScale.js";
import type { MuscleRegion } from "./schemas/muscle.js";
import { muscleRegion } from "./taxonomy.js";
import {
  SUBSTANTIAL_MUSCLE_CONTRIB_MIN,
  resolveExerciseMuscleWeights,
  type AggregatorExerciseRow,
} from "./volumeByMuscle.js";

// What a session trained, as a label key (#367, Antenore 17/10). Pure: the
// words live in i18n, the caller maps this to them. Counts LOADED work only:
// the hard sets (effectiveRir <= EFFORT_HARD_SET_RIR), credited per muscle
// and grouped by region. Light work does not name the session.

/** The first one or two regions hold at least this share of the credit: they name the session. */
export const DOMINANT_SHARE = 0.75;
/** Each half (upper, lower) holds at least this share of the upper+lower credit: Full body. */
export const HALF_SHARE = 0.25;

const UPPER_REGIONS: ReadonlySet<MuscleRegion> = new Set([
  "chest",
  "back",
  "shoulders",
  "arms",
]);
const LOWER_REGIONS: ReadonlySet<MuscleRegion> = new Set(["lower", "posterior"]);

export type WorkoutNameKind =
  | { kind: "regions"; regions: MuscleRegion[] }
  | { kind: "upper" | "lower" | "fullBody" | "deload" | "mobility" | "cardio" };

export type NamingSet = {
  // The effort the save would store: fail, and the canonical RIR. A saved
  // strength set always carries one; null only comes from cardio (no credit). An AMRAP is just a set with its result.
  fail?: boolean;
  rir: number | null;
  // An external load: a weight above zero on a weighted entry, or extra load
  // on a bodyweight one. Plain bodyweight is false.
  hasExternalLoad: boolean;
  // Did the set produce real work (volume or time under tension, as
  // isCountedLogSet)? A 0 kg set with a RIR does not name the session. Absent
  // means yes, so a caller without the metrics keeps the old reading (#531).
  counts?: boolean;
};

export type NamingEntry = {
  // null when the catalog row is unknown (no muscles to credit).
  row: AggregatorExerciseRow | null;
  isCardio: boolean;
  // COMPLETED sets only.
  sets: ReadonlyArray<NamingSet>;
};

const sumOf = (m: ReadonlyMap<MuscleRegion, number>, set: ReadonlySet<MuscleRegion>) => {
  let t = 0;
  for (const [r, v] of m) if (set.has(r)) t += v;
  return t;
};

export function classifyWorkout(
  entries: ReadonlyArray<NamingEntry>,
): WorkoutNameKind | null {
  const credit = new Map<MuscleRegion, number>();
  let anySet = false;
  let anyCardio = false;
  let anyNonCardio = false;
  let anyLoad = false;
  let anyHard = false;
  for (const e of entries) {
    if (e.sets.length === 0) continue;
    anySet = true;
    if (e.isCardio) {
      anyCardio = true;
      continue;
    }
    anyNonCardio = true;
    // The name keeps AP-6's step, not #538's credit: it is no count against a band.
    const credits = (e.row ? resolveExerciseMuscleWeights(e.row) : []).map(
      ([m, w]) => [m, w >= SUBSTANTIAL_MUSCLE_CONTRIB_MIN ? 1 : w] as const,
    );
    for (const s of e.sets) {
      if (s.counts === false) continue;
      if (s.hasExternalLoad) anyLoad = true;
      const rir = effectiveRir(s);
      if (rir === null || rir > EFFORT_HARD_SET_RIR) continue;
      anyHard = true;
      for (const [muscle, c] of credits) {
        const r = muscleRegion(muscle);
        if (!r || r === "cardio") continue;
        credit.set(r, (credit.get(r) ?? 0) + c);
      }
    }
  }
  if (!anySet) return null;
  const total = [...credit.values()].reduce((a, b) => a + b, 0);
  if (total <= 0) {
    // Hard sets that credit no region (unknown row): no honest label, date only.
    if (anyHard) return null;
    if (anyCardio && !anyNonCardio) return { kind: "cardio" };
    return { kind: anyLoad ? "deload" : "mobility" };
  }
  const ranked = [...credit.entries()].sort((a, b) => b[1] - a[1]);
  const top2 = ranked.slice(0, 2);
  const top1Share = top2[0]![1] / total;
  if (top1Share >= DOMINANT_SHARE) return { kind: "regions", regions: [top2[0]![0]] };
  if (top2.length === 2 && (top2[0]![1] + top2[1]![1]) / total >= DOMINANT_SHARE) {
    return { kind: "regions", regions: top2.map(([r]) => r) };
  }
  // Core counts for neither half.
  const upper = sumOf(credit, UPPER_REGIONS);
  const lower = sumOf(credit, LOWER_REGIONS);
  const halves = upper + lower;
  if (upper / halves >= HALF_SHARE && lower / halves >= HALF_SHARE) {
    return { kind: "fullBody" };
  }
  return { kind: upper >= lower ? "upper" : "lower" };
}
